import React, {
  forwardRef,
  ForwardRefExoticComponent,
  isValidElement,
  PropsWithChildren,
  PropsWithoutRef,
  ReactElement,
  RefAttributes,
  useEffect,
  useState,
} from 'react'
import { Platform, Pressable, StyleProp, StyleSheet, View, ViewProps, ViewStyle } from 'react-native'
import { Color, Icon, Radius, Spacing, Text } from 'zens-storybook'

import InfoHead from './head'

const styles = StyleSheet.create({
  container: {
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
    paddingVertical: Spacing.M,
    paddingHorizontal: Spacing.M + Spacing.S,
  },
  top: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  content: {
    flex: 1,
  },
  wrapper: {
    marginBottom: Spacing.S,
  },
  collapsable: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  head: {
    flex: 1,
    flexDirection: 'row',
  },
  unfoldIcon: {
    marginLeft: -Spacing.S,
    width: Spacing.S,
    alignItems: 'flex-start',
  },
  left: {
    flex: 4,
  },
  skeletonTop: {
    alignItems: 'center',
  },
})

interface InfoBlockTitle {
  text?: string | ReactElement
  left?: ReactElement
  right?: ReactElement
}

interface Props extends ViewProps {
  title?: string | ReactElement | InfoBlockTitle
  left?: ReactElement
  right?: ReactElement
  bottom?: ReactElement
  unfolded?: boolean
  contentStyle?: StyleProp<ViewStyle>
}
const InfoBlock = forwardRef<View, PropsWithChildren<Props>>((props, ref) => {
  const { title, right, left, bottom, contentStyle, children, style, unfolded: infoUnfolded, ...otherProps } = props
  const [unfolded, setUnfolded] = useState(!!infoUnfolded)

  useEffect(() => {
    setUnfolded(!!infoUnfolded)
  }, [infoUnfolded])

  const toggleOpen = () => {
    setUnfolded(!unfolded)
  }

  const renderHead = () => {
    if (typeof title === 'string') return <InfoHead title={title} style={styles.head} />
    else if (isValidElement(title)) return title
    const { left, text, right } = title as InfoBlockTitle
    return (
      <InfoHead title={text} style={styles.head}>
        {left && <View style={styles.left}>{left}</View>}
        {right}
      </InfoHead>
    )
  }

  const renderCollapsable = () => {
    return (
      <View style={styles.collapsable}>
        <View style={styles.unfoldIcon}>
          <Icon name={unfolded ? 'right' : 'down'} size="XS" />
        </View>
        {renderHead()}
      </View>
    )
  }
  return (
    <View ref={ref} style={[styles.container, style]} {...otherProps}>
      <View style={styles.top}>
        {left}
        <View style={[styles.content, contentStyle]}>
          {title && (
            <View style={!unfolded && !!children && styles.wrapper}>
              {infoUnfolded !== undefined ? (
                <Pressable
                  onPress={toggleOpen}
                  style={Platform.select({
                    // @ts-expect-error outlineStyle is not a valid style property
                    web: {
                      outlineStyle: 'none',
                    } as ViewStyle,
                  })}
                  testID="InfoBlock.Pressable"
                >
                  {renderCollapsable}
                </Pressable>
              ) : (
                renderHead()
              )}
            </View>
          )}
          {!unfolded && children}
        </View>
        {right}
      </View>
      {bottom}
    </View>
  )
}) as ForwardRefExoticComponent<PropsWithoutRef<Props> & RefAttributes<View>> & {
  Skeleton: (props: SkeletonProps) => ReactElement
}

interface SkeletonProps extends ViewProps {
  title?: string | ReactElement | true
  left?: ReactElement
  right?: ReactElement
  bottom?: ReactElement
  contentStyle?: StyleProp<ViewStyle>
}
InfoBlock.Skeleton = (props: SkeletonProps) => {
  const { title, left, right, bottom, contentStyle, style, children, ...otherProps } = props
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={[styles.top, styles.skeletonTop]}>
        {left}
        <View style={[styles.content, contentStyle]}>
          {title && (
            <View style={!!children && styles.wrapper}>
              {title === true ? (
                <Text.Skeleton level={4} width={299} />
              ) : typeof title === 'string' ? (
                <Text level={4}>{title}</Text>
              ) : (
                title
              )}
            </View>
          )}
          {children}
        </View>
        {right}
      </View>
      {bottom}
    </View>
  )
}

export default InfoBlock
